import React, { useEffect, useMemo, useState } from "react";
import api from "../api/api";

import {
    Box,
    Typography,
    CircularProgress,
    Grid,
    Card,
    CardContent,
    Divider,
    Chip,
    Stack,
    Alert,
    Button,
} from "@mui/material";

import {
    FaBook,
    FaChartLine,
    FaClipboardList,
    FaMoneyBillWave,
    FaFileAlt,
    FaUsers,
    FaArrowUp,
    FaGraduationCap,
} from "react-icons/fa";

import {
    Bar,
} from "react-chartjs-2";

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
} from "chart.js";

import LinkStudents from "./LinkStudents";
import TeacherSubjectData from "../pages/Teachers/TeacherSubjectData";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend
);

const TeacherAnalytics = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError("");

            const response = await api.get(
                "/teacher-dashboard/"
            );

            setData(response.data || {});
        } catch (err) {
            console.error(
                "Error fetching teacher analytics:",
                err
            );

            setError(
                err?.response?.data?.detail ||
                    "Unable to load teacher analytics. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const formatNumber = (value) => {
        const number = Number(value || 0);

        return number.toLocaleString();
    };

    const formatCurrency = (value) => {
        return `$${Number(
            value || 0
        ).toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        })}`;
    };

    const summaryItems = useMemo(
        () => [
            {
                label: "Salary",
                value: formatCurrency(
                    data?.total_salary
                ),
                description: "Current salary",
                icon: <FaMoneyBillWave />,
                color: "#16a34a",
                background: "#ecfdf3",
            },
            {
                label: "Deductions",
                value: formatCurrency(
                    data?.total_deductions
                ),
                description: "Total deductions",
                icon: <FaArrowUp />,
                color: "#dc2626",
                background: "#fef2f2",
            },
            {
                label: "Assignments",
                value: formatNumber(
                    data?.assignment_count
                ),
                description: "Assignments created",
                icon: <FaClipboardList />,
                color: "#2563eb",
                background: "#eff6ff",
            },
            {
                label: "Lesson Plans",
                value: formatNumber(
                    data?.lessonplan_count
                ),
                description: "Plans prepared",
                icon: <FaFileAlt />,
                color: "#ea580c",
                background: "#fff7ed",
            },
        ],
        [data]
    );

    const chartData = useMemo(
        () => ({
            labels: [
                "Salary",
                "Deductions",
                "Assignments",
                "Lesson Plans",
            ],
            datasets: [
                {
                    label: "Teacher Activity",
                    data: [
                        Number(
                            data?.total_salary || 0
                        ),
                        Number(
                            data?.total_deductions || 0
                        ),
                        Number(
                            data?.assignment_count || 0
                        ),
                        Number(
                            data?.lessonplan_count || 0
                        ),
                    ],
                    backgroundColor: [
                        "#16a34a",
                        "#dc2626",
                        "#2563eb",
                        "#ea580c",
                    ],
                    borderRadius: 10,
                    borderSkipped: false,
                    barPercentage: 0.65,
                    categoryPercentage: 0.65,
                },
            ],
        }),
        [data]
    );

    const chartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,

            interaction: {
                mode: "index",
                intersect: false,
            },

            plugins: {
                legend: {
                    display: false,
                },

                tooltip: {
                    backgroundColor: "#0f172a",
                    titleColor: "#ffffff",
                    bodyColor: "#e2e8f0",
                    borderColor: "#334155",
                    borderWidth: 1,
                    padding: 13,
                    cornerRadius: 10,

                    titleFont: {
                        size: 13,
                        weight: "700",
                    },

                    bodyFont: {
                        size: 13,
                        weight: "500",
                    },

                    displayColors: true,
                },
            },

            scales: {
                x: {
                    border: {
                        display: false,
                    },

                    grid: {
                        display: false,
                    },

                    ticks: {
                        color: "#64748b",
                        font: {
                            size: 12,
                            weight: "600",
                        },
                        padding: 8,
                    },
                },

                y: {
                    beginAtZero: true,

                    border: {
                        display: false,
                    },

                    grid: {
                        color: "rgba(148,163,184,0.16)",
                    },

                    ticks: {
                        color: "#64748b",
                        font: {
                            size: 11,
                        },
                        padding: 8,
                    },
                },
            },
        }),
        []
    );

    if (loading) {
        return (
            <Box
                sx={{
                    minHeight: 420,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 2,
                }}
            >
                <Card
                    elevation={0}
                    sx={{
                        width: "100%",
                        maxWidth: 420,
                        borderRadius: 4,
                        border:
                            "1px solid #e2e8f0",
                        background:
                            "rgba(255,255,255,0.96)",
                    }}
                >
                    <CardContent
                        sx={{
                            py: 6,
                            textAlign: "center",
                        }}
                    >
                        <Box
                            sx={{
                                width: 58,
                                height: 58,
                                mx: "auto",
                                mb: 2,
                                borderRadius: 3,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background:
                                    "#eff6ff",
                                color: "#2563eb",
                            }}
                        >
                            <CircularProgress
                                size={28}
                                thickness={4}
                                color="primary"
                            />
                        </Box>

                        <Typography
                            sx={{
                                fontSize: "1.05rem",
                                fontWeight: 800,
                                color: "#172033",
                            }}
                        >
                            Loading teacher analytics
                        </Typography>

                        <Typography
                            sx={{
                                mt: 0.7,
                                fontSize: "0.9rem",
                                color: "#64748b",
                            }}
                        >
                            Preparing your teaching overview...
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    width: "100%",
                    py: 4,
                }}
            >
                <Alert
                    severity="error"
                    sx={{
                        borderRadius: 3,
                        alignItems: "center",
                    }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={
                                fetchDashboardData
                            }
                        >
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: "100%",
                minHeight: "100%",
                background:
                    "linear-gradient(180deg,#f8fafc 0%,#f1f5f9 100%)",
                px: {
                    xs: 1,
                    sm: 2,
                    md: 3,
                    lg: 4,
                },
                py: {
                    xs: 1.5,
                    sm: 2.5,
                    md: 3,
                },
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: 1600,
                    mx: "auto",
                }}
            >
                {/* =====================================================
                    HEADER
                ====================================================== */}

                <Box
                    sx={{
                        mb: {
                            xs: 2.5,
                            md: 3.5,
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: {
                                xs: "flex-start",
                                sm: "center",
                            },
                            justifyContent:
                                "space-between",
                            gap: 2,
                            flexWrap: "wrap",
                        }}
                    >
                        <Box>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{
                                    mb: 1,
                                    flexWrap:
                                        "wrap",
                                }}
                            >
                                <Chip
                                    icon={
                                        <FaChartLine />
                                    }
                                    label="Teaching Analytics"
                                    size="small"
                                    sx={{
                                        height: 30,
                                        borderRadius:
                                            2,
                                        background:
                                            "#eaf3ff",
                                        color: "#1677ff",
                                        fontWeight: 800,
                                        "& .MuiChip-icon":
                                            {
                                                color:
                                                    "#1677ff",
                                            },
                                    }}
                                />
                            </Stack>

                            <Typography
                                sx={{
                                    fontSize: {
                                        xs: "1.45rem",
                                        sm: "1.75rem",
                                        md: "2rem",
                                    },
                                    lineHeight: 1.2,
                                    fontWeight: 850,
                                    letterSpacing:
                                        "-0.5px",
                                    color: "#172033",
                                }}
                            >
                                Teacher Analytics
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.8,
                                    maxWidth: 700,
                                    color: "#64748b",
                                    fontSize: {
                                        xs: "0.88rem",
                                        sm: "0.95rem",
                                    },
                                    lineHeight: 1.6,
                                }}
                            >
                                Monitor your teaching
                                activities, academic
                                resources and teacher
                                performance from one
                                workspace.
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                width: 52,
                                height: 52,
                                borderRadius: 3,
                                display: {
                                    xs: "none",
                                    sm: "flex",
                                },
                                alignItems: "center",
                                justifyContent:
                                    "center",
                                background:
                                    "linear-gradient(135deg,#1677ff,#4f46e5)",
                                color: "#ffffff",
                                boxShadow:
                                    "0 12px 25px rgba(22,119,255,.20)",
                            }}
                        >
                            <FaChartLine
                                size={22}
                            />
                        </Box>
                    </Box>
                </Box>

                {/* =====================================================
                    SUMMARY CARDS
                ====================================================== */}

                <Grid
                    container
                    spacing={{
                        xs: 1.5,
                        sm: 2,
                        md: 2.5,
                    }}
                    sx={{
                        mb: {
                            xs: 2,
                            md: 2.5,
                        },
                    }}
                >
                    {summaryItems.map(
                        (item) => (
                            <Grid
                                item
                                xs={6}
                                sm={6}
                                md={3}
                                key={item.label}
                            >
                                <Card
                                    elevation={0}
                                    sx={{
                                        height: "100%",
                                        minHeight: {
                                            xs: 142,
                                            sm: 155,
                                        },
                                        borderRadius: 3,
                                        border:
                                            "1px solid #e2e8f0",
                                        background:
                                            "#ffffff",
                                        transition:
                                            "all .25s ease",
                                        "&:hover":
                                            {
                                                transform:
                                                    "translateY(-3px)",
                                                boxShadow:
                                                    "0 14px 30px rgba(15,23,42,.08)",
                                            },
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            p: {
                                                xs: 1.7,
                                                sm: 2.2,
                                            },
                                            "&:last-child":
                                                {
                                                    pb: {
                                                        xs: 1.7,
                                                        sm: 2.2,
                                                    },
                                                },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "flex-start",
                                                justifyContent:
                                                    "space-between",
                                                gap: 1,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: {
                                                        xs: 38,
                                                        sm: 44,
                                                    },
                                                    height: {
                                                        xs: 38,
                                                        sm: 44,
                                                    },
                                                    borderRadius:
                                                        2.5,
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    justifyContent:
                                                        "center",
                                                    background:
                                                        item.background,
                                                    color:
                                                        item.color,
                                                    fontSize: {
                                                        xs: 16,
                                                        sm: 18,
                                                    },
                                                }}
                                            >
                                                {
                                                    item.icon
                                                }
                                            </Box>

                                            <FaArrowUp
                                                style={{
                                                    color:
                                                        "#cbd5e1",
                                                    fontSize: 11,
                                                }}
                                            />
                                        </Box>

                                        <Typography
                                            sx={{
                                                mt: 1.8,
                                                color:
                                                    "#64748b",
                                                fontSize: {
                                                    xs: "0.72rem",
                                                    sm: "0.8rem",
                                                },
                                                fontWeight: 700,
                                            }}
                                        >
                                            {
                                                item.label
                                            }
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 0.35,
                                                color:
                                                    "#172033",
                                                fontSize: {
                                                    xs: "1.05rem",
                                                    sm: "1.3rem",
                                                },
                                                lineHeight: 1.25,
                                                fontWeight: 850,
                                                wordBreak:
                                                    "break-word",
                                            }}
                                        >
                                            {
                                                item.value
                                            }
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 0.45,
                                                color:
                                                    "#94a3b8",
                                                fontSize: {
                                                    xs: "0.67rem",
                                                    sm: "0.72rem",
                                                },
                                            }}
                                        >
                                            {
                                                item.description
                                            }
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )
                    )}
                </Grid>

                {/* =====================================================
                    INFORMATION + CHART
                ====================================================== */}

                <Grid
                    container
                    spacing={{
                        xs: 2,
                        md: 2.5,
                    }}
                    sx={{
                        mb: {
                            xs: 2,
                            md: 2.5,
                        },
                    }}
                >
                    <Grid
                        item
                        xs={12}
                        lg={4}
                    >
                        <Card
                            elevation={0}
                            sx={{
                                height: "100%",
                                borderRadius: 3.5,
                                border:
                                    "1px solid #e2e8f0",
                                background:
                                    "#ffffff",
                                overflow:
                                    "hidden",
                                boxShadow:
                                    "0 8px 25px rgba(15,23,42,.045)",
                            }}
                        >
                            <Box
                                sx={{
                                    px: {
                                        xs: 2,
                                        sm: 2.5,
                                    },
                                    py: 2,
                                    background:
                                        "linear-gradient(135deg,#1677ff,#4f46e5)",
                                    color: "#ffffff",
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1.2}
                                    alignItems="center"
                                >
                                    <Box
                                        sx={{
                                            width: 38,
                                            height: 38,
                                            borderRadius: 2,
                                            display:
                                                "flex",
                                            alignItems:
                                                "center",
                                            justifyContent:
                                                "center",
                                            background:
                                                "rgba(255,255,255,.15)",
                                        }}
                                    >
                                        <FaGraduationCap />
                                    </Box>

                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize:
                                                    "1rem",
                                                fontWeight:
                                                    800,
                                            }}
                                        >
                                            Teacher Information
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 0.2,
                                                fontSize:
                                                    "0.75rem",
                                                color:
                                                    "rgba(255,255,255,.78)",
                                            }}
                                        >
                                            Your assigned
                                            academic details
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            <CardContent
                                sx={{
                                    p: {
                                        xs: 1.5,
                                        sm: 2,
                                    },
                                }}
                            >
                                <TeacherSubjectData />
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        lg={8}
                    >
                        <Card
                            elevation={0}
                            sx={{
                                height: "100%",
                                borderRadius: 3.5,
                                border:
                                    "1px solid #e2e8f0",
                                background:
                                    "#ffffff",
                                boxShadow:
                                    "0 8px 25px rgba(15,23,42,.045)",
                            }}
                        >
                            <CardContent
                                sx={{
                                    p: {
                                        xs: 2,
                                        sm: 2.5,
                                        md: 3,
                                    },
                                    "&:last-child":
                                        {
                                            pb: {
                                                xs: 2,
                                                sm: 2.5,
                                                md: 3,
                                            },
                                        },
                                }}
                            >
                                <Box
                                    sx={{
                                        display:
                                            "flex",
                                        alignItems:
                                            "flex-start",
                                        justifyContent:
                                            "space-between",
                                        gap: 2,
                                        mb: 2.5,
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize:
                                                    {
                                                        xs: "1.05rem",
                                                        sm: "1.2rem",
                                                    },
                                                fontWeight:
                                                    850,
                                                color:
                                                    "#172033",
                                            }}
                                        >
                                            Activity Overview
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 0.45,
                                                fontSize:
                                                    {
                                                        xs: "0.78rem",
                                                        sm: "0.84rem",
                                                    },
                                                color:
                                                    "#64748b",
                                            }}
                                        >
                                            Financial and
                                            academic activity
                                            at a glance
                                        </Typography>
                                    </Box>

                                    <Chip
                                        label="Overview"
                                        size="small"
                                        sx={{
                                            display: {
                                                xs: "none",
                                                sm: "flex",
                                            },
                                            borderRadius:
                                                2,
                                            background:
                                                "#f1f5f9",
                                            color:
                                                "#475569",
                                            fontWeight: 700,
                                        }}
                                    />
                                </Box>

                                <Divider
                                    sx={{
                                        mb: 2,
                                        borderColor:
                                            "#eef2f7",
                                    }}
                                />

                                <Box
                                    sx={{
                                        width: "100%",
                                        height: {
                                            xs: 280,
                                            sm: 340,
                                            md: 380,
                                        },
                                    }}
                                >
                                    <Bar
                                        data={
                                            chartData
                                        }
                                        options={
                                            chartOptions
                                        }
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* =====================================================
                    STUDENT MANAGEMENT
                ====================================================== */}

 <Card
    elevation={0}
    sx={{
        borderRadius: 3.5,
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        boxShadow:
            "0 8px 25px rgba(15,23,42,.045)",
        overflow: "hidden",
        transition:
            "transform .2s ease, box-shadow .2s ease",
        "&:hover": {
            transform: "translateY(-2px)",
            boxShadow:
                "0 12px 30px rgba(15,23,42,.07)",
        },
    }}
>
    <CardContent
        sx={{
            p: {
                xs: 2,
                sm: 2.6,
                md: 3,
            },
        }}
    >
        <Box
            sx={{
                display: "flex",
                alignItems: {
                    xs: "flex-start",
                    sm: "center",
                },
                justifyContent: "space-between",
                gap: 2,
                mb: 2.2,
                flexWrap: "wrap",
            }}
        >
       

            
        </Box>

        <Divider
            sx={{
                mb: 2.5,
                borderColor: "#eef2f7",
            }}
        />

        <LinkStudents />
    </CardContent>
</Card>
            </Box>
        </Box>
    );
};

export default TeacherAnalytics;
